import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAnalyticsGraphQLClient } from '../../providers';
import { IUseAnalyticsGraphQueryProps } from './types';

/**
 * Custom use query hook used to pair request with react-query patterns
 */
export const useAnalyticsGraphQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = any,
  TVariables = any
>({
  key,
  gql,
  variables,
  validateClientResponseData,
  responseTForm,
  requestHeaders,
  ...rest
}: IUseAnalyticsGraphQueryProps<TQueryFnData, TError, TData, TVariables>) => {
  const client = useAnalyticsGraphQLClient();
  const queryClient = useQueryClient();

  const request = React.useCallback(async () => {
    const response = await client!.request<any, TVariables>(gql, variables, requestHeaders);

    if (validateClientResponseData && !validateClientResponseData(response)) {
      throw new Error('Response is invalid');
    }

    return responseTForm ? responseTForm(response) : response;
  }, [client, gql, validateClientResponseData, variables, responseTForm, requestHeaders]);

  return {
    invalidate: () => {
      queryClient.invalidateQueries(key);
    },
    ...useQuery<TQueryFnData, TError, TData>(key, () => request(), {
      refetchOnWindowFocus: false,
      ...rest,
    }),
  };
};
